import React, { useState, useEffect } from 'react';
import { Image } from 'react-native-web';
import { Box, Inline, BoxSizeOptions } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import formatProfilePhoto from '../../../modules/formatters/formatProfilePhoto';
import {
  Stack, Absolute, BodyText,
} from '../../ui';

type ProgressBarProps = {
  url?: string;
  criteriaSatisfied?: number;
  maxHeight?: BoxSizeOptions;
};

export const TOTAL_CRITERIA = 8;

const ProgressBar: React.FC<ProgressBarProps> = ({
  url,
  criteriaSatisfied,
  maxHeight,
}) => {
  const [barWidth, setBarWidth] = useState<string>('0%');
  const [barHeight] = useState<BoxSizeOptions>(maxHeight || 12);
  const [barRadius, setBarRadius] = useState<number>();
  const [imageSize, setImageSize] = useState<number>();
  const [strokeColor, setStrokeColor] = useState<string>(colors.red);
  const [imageOffset, setImageOffset] = useState<number>(0);
  const formattedURL = url ? formatProfilePhoto(url) : null;

  useEffect(() => {
    setBarWidth(`${criteriaSatisfied * (100 / TOTAL_CRITERIA)}%`);
    if (criteriaSatisfied <= 2) {
      setImageOffset(0);
      setStrokeColor(colors.red);
    } else if (criteriaSatisfied >= TOTAL_CRITERIA / 2 && criteriaSatisfied < TOTAL_CRITERIA - 1) {
      setImageOffset(barHeight as number);
      setStrokeColor(colors.sunshine);
    } else if (criteriaSatisfied >= TOTAL_CRITERIA - 1) {
      setImageOffset(barHeight as number * 2);
      setStrokeColor(colors.green);
    }
  }, [criteriaSatisfied]);

  useEffect(() => {
    const height = barHeight as number;
    setBarRadius(height / 2);
    setImageSize(height * 2);
  }, [barHeight]);

  return (
    <Stack space={8}>
      <Box minWidth="100%">
        <Box color={colors.lightGrey} minWidth="100%" minHeight={barHeight} borderRadius={6} />
        <Absolute minWidth="100%" minHeight={barHeight}>
          <Box
            style={{
              backgroundImage: 'linear-gradient(89.66deg, #F9484A -5%, #f2c94c 36%, #f2c94c 66%, #30a55e 105%)',
            }}
            minWidth="100%"
            minHeight={barHeight}
            borderRadius={barRadius}
          />
        </Absolute>
        <Absolute
          minWidth={`calc(100% - ${barWidth})`}
          minHeight={barHeight}
          left={barWidth}
          testID="search-rank-progress-bar"
        >
          <Box
            color={colors.lightGrey}
            minWidth="100%"
            minHeight={barHeight}
            borderRadius={barRadius}
            borderColor={colors.whiteWash}
            borderWidth={1}
          />
        </Absolute>
        <Absolute
          top={0 - (barHeight as number / 2)}
          left={`calc(${barWidth} - ${imageOffset}px)`}
          style={{
            width: 24,
            height: 24,
            borderRadius: 30,
            backgroundColor: colors.whiteWash,
          }}
        >
          {formattedURL && (
            <Image
              source={{ uri: formattedURL }}
              style={{
                width: imageSize,
                height: imageSize,
                borderRadius: imageSize,
                borderColor: strokeColor,
                borderWidth: 1,
              }}
            />
          )}
        </Absolute>
      </Box>
      <Inline minWidth="100%">
        <Box minWidth="33%">
          <BodyText
            weight={criteriaSatisfied === 0 ? 'bold' : 'normal'}
            appearance="small"
            testID="search-rank-progress-text-below-average"
          >
            Below Average
          </BodyText>
        </Box>
        <Box minWidth="33%">
          <BodyText
            textAlign="center"
            weight={criteriaSatisfied === TOTAL_CRITERIA / 2 ? 'bold' : 'normal'}
            appearance="small"
            testID="search-rank-progress-text-average"
          >
            Average
          </BodyText>
        </Box>
        <Box minWidth="33%">
          <BodyText
            textAlign="right"
            weight={criteriaSatisfied === TOTAL_CRITERIA ? 'bold' : 'normal'}
            appearance="small"
            testID="search-rank-progress-text-top-pro"
          >
            Top Pro
          </BodyText>
        </Box>
      </Inline>
    </Stack>
  );
};

export default ProgressBar;
